import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { InterestLine, InterestRun } from '../../interfaces/borrowing.interface';
import { BorrowingService } from '../../services/borrowing.service';
import { AuthService } from '../../services/auth.service';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { useToast } from '../../context/ToastContext';
import { formatDate, firstOfYearLocalIso, toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { ConfirmDialog } from '../common/ConfirmDialog';

/** Previous calendar month as yyyy-MM (what the scheduler runs on the 1st). */
export const previousMonth = (now: Date = new Date()): string => {
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const InterestRuns: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution()!;
  const canManage = usePermission(PERMISSIONS.FINANCING_MANAGE);
  const [month, setMonth] = useState(previousMonth());
  const [runs, setRuns] = useState<InterestRun[]>([]);
  const [result, setResult] = useState<InterestRun | null>(null);
  const [register, setRegister] = useState<InterestLine[]>([]);
  const [from, setFrom] = useState(firstOfYearLocalIso());
  const [to, setTo] = useState(toLocalIsoDate());
  const [busy, setBusy] = useState(false);
  const [voidingLine, setVoidingLine] = useState<InterestLine | null>(null);
  const [voidBusy, setVoidBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, reg] = await Promise.all([BorrowingService.listRuns(institutionId), BorrowingService.interestRegister(institutionId, from, to)]);
      setRuns(r); setRegister(reg);
    } catch (err) {
      toast.showApiError(err, 'Could not load interest runs');
    }
  }, [institutionId, from, to, toast]);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setBusy(true);
    try {
      const r = await BorrowingService.runInterest(institutionId, `${month}-01`);
      setResult(r);
      toast.showSuccess('Interest run finished');
      await load();
    } catch (err) {
      toast.showApiError(err, 'Could not run interest');
    } finally {
      setBusy(false);
    }
  };

  const confirmVoidLine = async () => {
    if (!voidingLine || voidBusy) return; // busy guard: a double click must not send two voids
    setVoidBusy(true);
    try {
      await BorrowingService.voidInterestLine(voidingLine.id);
      toast.showSuccess('Interest line voided');
      setVoidingLine(null);
      await load();
    } catch (err) {
      toast.showApiError(err, 'Could not void the interest line');
      setVoidingLine(null);
    } finally {
      setVoidBusy(false);
    }
  };

  const failed = result?.lines.filter(l => l.status === 'FAILED') ?? [];

  return (
    <Box data-help-page="financing.interest.page" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ flex: '1 1 240px' }}>Borrowing interest</Typography>
        <Button component={RouterLink} to="/financing">Facilities</Button>
      </Box>
      <Paper data-help-section="financing.interest.run" sx={{ p: 2, mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Interest accrues monthly (also run automatically on the 1st for the previous month). Re-running a month only posts facilities not already posted.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <TextField data-help="financing.interest.month" sx={{ flex: '1 1 180px', maxWidth: 220 }} size="small" type="month" label="Month" InputLabelProps={{ shrink: true }}
            value={month} onChange={e => setMonth(e.target.value)} />
          {canManage && <Button data-help="financing.interest.runNow" variant="contained" onClick={runNow} disabled={!month || busy}>Run now</Button>}
        </Box>
        {result && (
          <Alert severity={failed.length ? 'warning' : 'success'} sx={{ mt: 2 }} data-testid="run-result">
            {result.lines.length === 0 ? 'No open facilities to accrue interest for.' :
              `Run ${formatDate(result.period)}: ${formatMoney(result.totalInterest)} interest, ${result.lines.length} facility line(s)${failed.length ? `, ${failed.length} failed` : ''}.`}
            {failed.map(l => <div key={l.id}>{l.facilityNo}: {l.error}</div>)}
          </Alert>
        )}
      </Paper>

      <Typography variant="h6" sx={{ mb: 1 }}>Runs</Typography>
      <TableContainer data-help-section="financing.interest.runs" component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead><TableRow><TableCell data-help="financing.interest.runs.col.period">Period</TableCell><TableCell data-help="financing.interest.runs.col.trigger">Trigger</TableCell><TableCell data-help="financing.interest.runs.col.status">Status</TableCell><TableCell data-help="financing.interest.runs.col.interest" align="right">Interest</TableCell></TableRow></TableHead>
          <TableBody>
            {runs.map(r => (
              <TableRow key={r.id ?? r.period}><TableCell>{formatDate(r.period)}</TableCell><TableCell>{humanizeEnum(r.triggerSource)}</TableCell>
                <TableCell>{humanizeEnum(r.status)}</TableCell><TableCell align="right">{formatMoney(r.totalInterest)}</TableCell></TableRow>
            ))}
            {runs.length === 0 && <TableRow><TableCell colSpan={4}>No runs yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mb: 1 }}>Interest register</Typography>
      <Box data-help-section="financing.interest.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <TextField data-help="financing.interest.from" sx={{ flex: '1 1 160px', maxWidth: 200 }} size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={from} onChange={e => setFrom(e.target.value)} />
        <TextField data-help="financing.interest.to" sx={{ flex: '1 1 160px', maxWidth: 200 }} size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={to} onChange={e => setTo(e.target.value)} />
      </Box>
      <TableContainer data-help-section="financing.interest.register" component={Paper}>
        <Table size="small">
          <TableHead><TableRow><TableCell data-help="financing.interest.col.period">Period</TableCell><TableCell data-help="financing.interest.col.facility">Facility</TableCell><TableCell data-help="financing.interest.col.days" align="right">Days</TableCell>
            <TableCell data-help="financing.interest.col.interest" align="right">Interest</TableCell><TableCell data-help="financing.interest.col.status">Status</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {register.map(l => (
              <TableRow key={l.id}>
                <TableCell>{formatDate(l.period)}</TableCell><TableCell>{l.facilityNo} - {l.facilityName}</TableCell>
                <TableCell align="right">{l.days}</TableCell><TableCell align="right">{formatMoney(l.interestAmount)}</TableCell>
                <TableCell><Chip size="small" label={humanizeEnum(l.status)} color={l.status === 'POSTED' ? 'success' : l.status === 'FAILED' ? 'error' : 'default'} /></TableCell>
                <TableCell align="right">
                  {l.journalEntryId && <Button data-help="financing.interest.action.journal" size="small" component={RouterLink} to={`/accounting/journal-entries/${l.journalEntryId}`}>Journal</Button>}
                  {canManage && l.status === 'POSTED' && <Button data-help="financing.interest.action.void" size="small" color="error" onClick={() => setVoidingLine(l)}>Void</Button>}
                </TableCell>
              </TableRow>
            ))}
            {register.length === 0 && <TableRow><TableCell colSpan={6}>No interest in this range.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
      <ConfirmDialog open={!!voidingLine} title="Void this interest line?" confirmLabel="Void" confirmColor="error" submitting={voidBusy}
        message={voidingLine ? `Void the ${formatDate(voidingLine.period)} interest of ${voidingLine.facilityNo}? A reversing journal is posted.` : ''}
        onCancel={() => setVoidingLine(null)} onConfirm={confirmVoidLine} />
    </Box>
  );
};

export default InterestRuns;
